import React, { useState, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import styles from './PatientStories.module.css';
import markPImg from '../../../assets/ceo/before-after-mark-p.png';
import paulMImg from '../../../assets/ceo/before-after-paul-m.png';

const STORIES = [
    {
        image: markPImg,
        imageAlt: 'Mark P. before and after',
        content: (
            <>"I started in mid-October and I've reached a loss of 50 pounds. My goal was 70 over seven months — about 10 pounds a month — and I'm happy with the pace. I kept it sustainable: no eating after 8pm, no sugar, and 3 liters of water a day. Now I'm walking 2–3 miles daily and lifting light at the gym. <strong>This is life-changing.</strong>"</>
        ),
        cite: 'Mark P., 6 months in',
    },
    {
        image: paulMImg,
        imageAlt: 'Paul M. before and after — 280lb to 210lb',
        content: (
            <>"My daughter was 3 when I bent over to tie my shoe and let out an involuntary wheeze. That was it. I tried everything — workouts, keto, paleo, Weight Watchers. Then Toni suggested GLP-1s. <strong>I started losing instantaneously. And it was easy.</strong> Now I'm not afraid of mirrors, not scared I'll overflow into my neighbor's seat on a plane — and most importantly, I'll be around a lot longer for my daughter."</>
        ),
        cite: 'Paul M., 1+ year in · 90 lbs lost',
    },
    {
        image: null,
        content: (
            <>"I'm a 43-year-old high school football coach with three children. I was able to help others but, in the process, my health was sacrificed. The GLP medications enabled me to start my weight loss process, control my hunger and influence changes in diet and activity. <strong>I lost 33 pounds in 5 months</strong> and have more energy to be an even better father, coach, and member of our community."</>
        ),
        cite: 'Jim K., 5 months in · 33 lbs lost',
    },
    {
        image: null,
        content: (
            <>"I'm a 35-year-old female with rheumatoid arthritis. Joint pain and inflammation made it very difficult to partake in physical activity and even play with my children. The GLP-1 medications helped me lose 36 pounds in 4 months. <strong>What is equally as liberating as losing weight — I was able to stop taking my autoimmune medication</strong> because of the reduction in inflammation."</>
        ),
        cite: 'Allison H., 4 months in · 36 lbs lost',
    },
    {
        image: null,
        content: (
            <>"I'm a 28-year-old nurse with PCOS who was having trouble getting pregnant. My husband and I had tried for over a year and were considering a fertility specialist — even IVF. I used semaglutide to lose 29 pounds in 3 months. <strong>We were able to conceive within three months and now have a beautiful baby girl.</strong>"</>
        ),
        cite: 'Kayleigh S., 3 months in · 29 lbs lost',
    },
];

const TOTAL = STORIES.length;

function StoryCard({ story }) {
    return story.image ? (
        <div className={styles.card}>
            <div className={styles.photoSide}>
                <img src={story.image} alt={story.imageAlt} />
                <span className={styles.labelBefore}>Before</span>
                <span className={styles.labelAfter}>After</span>
            </div>
            <div className={styles.quoteSide}>
                <blockquote className={styles.quote}>{story.content}</blockquote>
                <cite className={styles.cite}>— {story.cite.toUpperCase()}</cite>
            </div>
        </div>
    ) : (
        <div className={styles.card}>
            <div className={styles.quoteSideFull}>
                <blockquote className={styles.quote}>{story.content}</blockquote>
                <cite className={styles.cite}>— {story.cite.toUpperCase()}</cite>
            </div>
        </div>
    );
}

function ChevronLeft() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}

function PatientStories() {
    const [currentIdx, setCurrentIdx] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    // Set when a dot is clicked, so the track can show the clicked story in the
    // slot it is about to slide into rather than the adjacent one.
    const [displayOverride, setDisplayOverride] = useState(null);
    const pendingTargetRef = useRef(null);
    const trackRef = useRef(null);

    // Returns [prevIdx, currentIdx, nextIdx] for the track
    const getDisplayIndices = (idx) => [
        (idx - 1 + TOTAL) % TOTAL,
        idx,
        (idx + 1) % TOTAL,
    ];

    const slide = useCallback((dir) => {
        if (isTransitioning || !trackRef.current) return;
        setIsTransitioning(true);

        // -66.666% = next slide (right), 0% = prev slide (left)
        const targetX = dir === 'next' ? -66.666 : 0;
        trackRef.current.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)';
        trackRef.current.style.transform = `translateX(${targetX}%)`;
    }, [isTransitioning]);

    const handleTransitionEnd = useCallback(() => {
        if (!trackRef.current) return;
        const isNext = trackRef.current.style.transform.includes('-66');

        trackRef.current.style.transition = 'none';

        // flushSync: update the slide content synchronously before resetting the
        // transform, so both changes land in the same browser paint (no flicker).
        flushSync(() => {
            // A dot click lands on its exact target; arrows step by one.
            const target = pendingTargetRef.current;
            pendingTargetRef.current = null;
            setCurrentIdx(prev => {
                if (target !== null) return target;
                return isNext ? (prev + 1) % TOTAL : (prev - 1 + TOTAL) % TOTAL;
            });
            setDisplayOverride(null);
            setIsTransitioning(false);
        });

        trackRef.current.style.transform = 'translateX(-33.333%)';
    }, []);

    const goTo = useCallback((newIdx) => {
        if (isTransitioning || newIdx === currentIdx) return;
        // Slide whichever way is shorter around the loop.
        const forward = (newIdx - currentIdx + TOTAL) % TOTAL;
        const dir = forward <= TOTAL / 2 ? 'next' : 'prev';

        pendingTargetRef.current = newIdx;
        // flushSync so the override is in the DOM before the transform starts —
        // otherwise the animation would reveal the adjacent story, not the target.
        flushSync(() => {
            setDisplayOverride(dir === 'next'
                ? [(currentIdx - 1 + TOTAL) % TOTAL, currentIdx, newIdx]
                : [newIdx, currentIdx, (currentIdx + 1) % TOTAL]);
        });
        slide(dir);
    }, [currentIdx, isTransitioning, slide]);

    const displayIndices = displayOverride ?? getDisplayIndices(currentIdx);

    return (
        <section className={styles.storiesSection}>
            <div className={styles.storiesHead}>
                <div className={styles.eyebrow}>What our patients say</div>
                <h2>Real people. Real results.</h2>
            </div>

            <div className={styles.carouselOuter}>
                <div className={styles.viewport}>
                    <div
                        ref={trackRef}
                        className={styles.track}
                        onTransitionEnd={handleTransitionEnd}
                    >
                        {displayIndices.map((storyIdx, pos) => (
                            <div key={`${pos}-${storyIdx}`} className={styles.slide}>
                                <StoryCard story={STORIES[storyIdx]} />
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.nav}>
                    <button className={styles.navBtn} onClick={() => slide('prev')} aria-label="Previous story">
                        <ChevronLeft />
                    </button>

                    <div className={styles.dots}>
                        {STORIES.map((_, i) => (
                            <button
                                key={i}
                                className={styles.dot + (i === currentIdx ? ' ' + styles.dotActive : '')}
                                onClick={() => goTo(i)}
                                aria-label={`Go to story ${i + 1}`}
                            />
                        ))}
                    </div>

                    <button className={styles.navBtn} onClick={() => slide('next')} aria-label="Next story">
                        <ChevronRight />
                    </button>
                </div>
            </div>
        </section>
    );
}

export default PatientStories;
